import React, { useRef, useState } from 'react';
import Stack from '~/components/shared/Layout/Stack';
import NextButton from '~/components/shared/Stepper/components/NextButton';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '~/hooks/useAppDispatch';
import { ApiStatus } from '~/store/types/sharedTypes';
import { RootState } from '~/store';
import { ModalFooter } from '~/components/shared/Modal/Modal';
import { Body, Box } from '@qred/components-library';
import useTranslate from '~/hooks/useTranslate';
import { getSignedUrl, postFile } from '~/services/api';
import { logToSentry } from '~/helpers/loggers.helper';
import { updateForm } from '~/store/slices/loanApplication.slice';
import { pushToGtmLoanAction } from '~/store/actions/gtmActions';
import { IFile } from '~/interfaces/shared';
import DocumentUpload from '~/components/shared/DocumentUpload/DocumentUpload';
import { validateFiles } from '~/helpers/validators.helper';
import DocumentList from '~/components/shared/DocumentUpload/DocumentList';
import DocumentErrorMessage, {
  FileUploadErrors,
} from '~/components/shared/DocumentUpload/DocumentErrorMessage';
import { filterOutIncorrectFiles } from '~/helpers/files.helper';
import useSubmit from '../../Steps/useSubmit';
import { CountryCode } from '@qred/shared-enums';

enum DocumentType {
  MT940 = 'mt940',
  Other = 'other',
}

type FileConfig = {
  [key in CountryCode]: {
    allowedFileExtensionsDisplayed: string[];
    allowedFileFormats: string[];
    maxSize: number;
    maxFiles: number;
    documentType: DocumentType;
  };
};

const fileConfigPerMarket: FileConfig = {
  [CountryCode.NL]: {
    allowedFileExtensionsDisplayed: ['.swi', '.STA', '.940', '.txt'],
    allowedFileFormats: ['text/plain'],
    maxSize: 15000000,
    documentType: DocumentType.MT940,
    maxFiles: 4,
  },
  [CountryCode.SE]: {
    allowedFileExtensionsDisplayed: [],
    allowedFileFormats: [],
    maxSize: 15000000,
    documentType: DocumentType.Other,
    maxFiles: 4,
  },
  [CountryCode.DK]: {
    allowedFileExtensionsDisplayed: [],
    allowedFileFormats: [],
    maxSize: 15000000,
    documentType: DocumentType.Other,
    maxFiles: 4,
  },
  [CountryCode.FI]: {
    allowedFileExtensionsDisplayed: [],
    allowedFileFormats: [],
    maxSize: 15000000,
    documentType: DocumentType.Other,
    maxFiles: 4,
  },
  [CountryCode.NO]: {
    allowedFileExtensionsDisplayed: [],
    allowedFileFormats: [],
    maxSize: 15000000,
    documentType: DocumentType.Other,
    maxFiles: 4,
  },
  [CountryCode.BE]: {
    allowedFileExtensionsDisplayed: [],
    allowedFileFormats: [],
    maxSize: 15000000,
    documentType: DocumentType.Other,
    maxFiles: 4,
  },
  [CountryCode.DE]: {
    allowedFileExtensionsDisplayed: [
      '.csv',
      '.xlsx',
      '.pdf',
      '.jpg',
      '.jpeg',
      '.png',
    ],
    allowedFileFormats: [
      'text/csv',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/pdf',
      'image/jpeg',
      'image/png',
    ],
    maxSize: 15000000,
    documentType: DocumentType.Other,
    maxFiles: 4,
  },
};

const UploadDocumentsModalBody = () => {
  const [documentsApiStatus, setDocumentsApiStatus] = useState(ApiStatus.Idle);
  const [localFiles, setLocalFiles] = useState<IFile[]>([]);
  const [
    localFileUploadErrors,
    setLocalFileUploadErrors,
  ] = useState<FileUploadErrors>({
    alreadyAdded: false,
    invalidType: false,
    tooLarge: false,
    maxLimit: false,
  });
  const [
    lastLocalUploadHadMultipleFiles,
    setLastLocalUploadHadMultipleFiles,
  ] = useState(false);
  const fileListRef = useRef<HTMLDivElement>(null);
  const onSubmit = useSubmit();
  const dispatch = useAppDispatch();
  const {
    loanApplication: { apiStatus, clientId },
    intl: { market },
  } = useSelector((state: RootState) => state);

  const {
    maxSize,
    maxFiles,
    allowedFileExtensionsDisplayed,
    allowedFileFormats,
  } = fileConfigPerMarket[market];

  const allowedFileFormatsAndExtensions = [
    ...allowedFileFormats,
    ...allowedFileExtensionsDisplayed,
  ];

  const t = useTranslate();

  const setErrors = (filesToCheck: IFile[]) => {
    const { hasAnyFormatIssue, hasAnySizeIssue } = validateFiles(
      filesToCheck,
      allowedFileFormatsAndExtensions,
      maxSize
    );

    const fileIsAlreadyAdded = filesToCheck.some(
      (file) => filesToCheck.filter((f) => f.name === file.name).length > 1
    );

    setLocalFileUploadErrors({
      alreadyAdded: fileIsAlreadyAdded,
      maxLimit: filesToCheck.length > maxFiles,
      invalidType: hasAnyFormatIssue,
      tooLarge: hasAnySizeIssue,
    });
  };

  const createFilesFromObjectUrls = (documents: IFile[]) =>
    Promise.all(
      documents.map(async (document) => {
        const res = await fetch(document.objectURL);
        const blob = await res.blob();
        return new File([blob], document.name, blob);
      })
    );

  const postFilesToS3 = async (files: File[]) => {
    const fileKeys = localFiles.map((localFile) => localFile.name);
    const res = await getSignedUrl(
      fileKeys,
      clientId ? String(clientId) : '',
      market,
      fileConfigPerMarket[market].documentType
    );
    await Promise.all(
      res.data.map(async (infoContainer: any, i: number) => {
        const method = infoContainer.method;

        if (method === 'POST') {
          const form = new FormData();
          Object.keys(infoContainer.fields).forEach((key) =>
            form.append(key, infoContainer.fields[key])
          );
          form.append('file', files[i]);
          await postFile(infoContainer.url, form, infoContainer.method);
        } else if (method === 'PUT') {
          await postFile(infoContainer.url, files[i], infoContainer.method);
        } else {
          throw new Error('Unsupported file upload method');
        }
      })
    );
    return res.data.map((file) => file.fields.key);
  };

  const uploadFilesToS3 = async () => {
    const files = await createFilesFromObjectUrls(localFiles);
    const filePaths = await postFilesToS3(files);
    return filePaths;
  };

  const uploadDocuments = async () => {
    setDocumentsApiStatus(ApiStatus.Started);
    try {
      const filePaths = await uploadFilesToS3();
      setDocumentsApiStatus(ApiStatus.Completed);
      dispatch(
        updateForm({
          bankFilesUploaded: true,
          bankFilesPaths: filePaths,
        })
      );
    } catch (err: any) {
      setDocumentsApiStatus(ApiStatus.Failed);
      logToSentry(err, 'uploadDocuments');
    }
  };

  const onSubmitButtonClick = async () => {
    dispatch(
      pushToGtmLoanAction({
        actionName: 'click_upload_documents_and_submit_button',
      })
    );

    await uploadDocuments();
    onSubmit();
  };

  const onSkipAndSubmitButtonClick = () => {
    dispatch(
      pushToGtmLoanAction({
        actionName: 'click_skip_upload_documents_and_submit_application_button',
      })
    );

    onSubmit();
  };

  const onLocalUploadFiles = (acceptedFiles: File[]) => {
    setLastLocalUploadHadMultipleFiles(acceptedFiles.length > 1);
    setDocumentsApiStatus(ApiStatus.Idle);

    const mappedFiles = acceptedFiles.map((file) => ({
      name: file.name,
      size: file.size,
      type: file.type,
      timestamp: file.lastModified,
      objectURL: URL.createObjectURL(file),
    }));

    setErrors(localFiles.concat(mappedFiles));
    setLocalFiles((currentFiles) =>
      filterOutIncorrectFiles(
        mappedFiles.concat(currentFiles),
        allowedFileFormatsAndExtensions,
        maxSize,
        maxFiles
      )
    );

    fileListRef.current?.scrollIntoView();
  };

  const handleRemoveFile = (file: IFile) => {
    setDocumentsApiStatus(ApiStatus.Idle);
    URL.revokeObjectURL(file.objectURL);
    const removedObjArray = localFiles.filter(
      (localFile) => localFile.objectURL !== file.objectURL
    );
    setLocalFiles(removedObjArray);
    setErrors(removedObjArray);
  };

  const onDeleteAll = () => {
    setDocumentsApiStatus(ApiStatus.Idle);
    localFiles.forEach((localFile) => {
      URL.revokeObjectURL(localFile.objectURL);
    });
    setLocalFiles([]);
    setErrors([]);
  };

  const maxFilesUploaded = localFiles.length >= maxFiles;

  return (
    <Stack spacing="md">
      <Body size="lg">{t('Onboarding.UploadFilesModalDescription')}</Body>
      {!maxFilesUploaded && (
        <DocumentUpload
          allowedFileExtensionsDisplayed={allowedFileExtensionsDisplayed}
          maxFiles={maxFiles}
          maxSize={maxSize}
          onUploadFiles={onLocalUploadFiles}
        />
      )}
      <Stack spacing="lg">
        <DocumentErrorMessage
          allowedFileExtensionsDisplayed={allowedFileExtensionsDisplayed}
          documentsApiStatus={documentsApiStatus}
          fileUploadErrors={localFileUploadErrors}
          lastLocalUploadHadMultipleFiles={lastLocalUploadHadMultipleFiles}
          maxFiles={maxFiles}
          maxSize={maxSize}
        />
        {!!localFiles.length && (
          <DocumentList
            fileListRef={fileListRef}
            files={localFiles}
            handleRemoveFile={handleRemoveFile}
            onDeleteAll={onDeleteAll}
          />
        )}
      </Stack>
      <ModalFooter fixedHeaderAndFooter filledBackground>
        <Box>
          <NextButton
            action={
              localFiles.length
                ? onSubmitButtonClick
                : onSkipAndSubmitButtonClick
            }
            label={
              localFiles.length
                ? 'Onboarding.Submit'
                : 'Onboarding.SkipAndSubmit'
            }
            isLoading={
              documentsApiStatus === ApiStatus.Started ||
              apiStatus.patch === ApiStatus.Started
            }
          />
        </Box>
      </ModalFooter>
    </Stack>
  );
};

export default UploadDocumentsModalBody;
